/**
 * Sprint Metrics Import — Smart Detection Engine
 *
 * Pure functions for parsing, detecting structure, mapping columns,
 * matching sprints, and parsing numbers from imported data.
 *
 * Input: string[][] (2D array from Excel file or pasted text)
 * Output: DetectionResult with mapped columns, matched sprints, and parsed values
 */

import * as XLSX from 'xlsx';

// =============================================================================
// TYPES
// =============================================================================

export type MetricField =
  | 'costsPlanned' | 'costsActual'
  | 'revenuePlanned' | 'revenueActual'
  | 'storyPointsPlanned' | 'storyPointsTarget' | 'storyPointsAchieved'
  | 'manDaysPlanned' | 'manDaysActual';

export const ALL_METRIC_FIELDS: MetricField[] = [
  'costsPlanned', 'costsActual',
  'revenuePlanned', 'revenueActual',
  'storyPointsPlanned', 'storyPointsTarget', 'storyPointsAchieved',
  'manDaysPlanned', 'manDaysActual',
];

export const METRIC_LABELS: Record<MetricField, string> = {
  costsPlanned: 'Costs Planned',
  costsActual: 'Costs Actual',
  revenuePlanned: 'Revenue Planned',
  revenueActual: 'Revenue Actual',
  storyPointsPlanned: 'SP Planned',
  storyPointsTarget: 'SP Target',
  storyPointsAchieved: 'SP Achieved',
  manDaysPlanned: 'Man-Days Planned',
  manDaysActual: 'Man-Days Actual',
};

export interface ColumnMapping {
  sourceIndex: number;
  sourceHeader: string;
  field: MetricField | '_sprintName' | null;
  confidence: number;
}

export interface SprintInfo {
  sprintId: string;
  name: string;
  sprintNumber: number;
  startDate: string;
  endDate: string;
}

export interface SprintMatch {
  rowIndex: number;
  sourceValue: string;
  sprint: SprintInfo | null;
  confidence: number;
}

export interface ParsedMetricRow {
  sprintMatch: SprintMatch;
  values: Partial<Record<MetricField, number | null>>;
}

export interface DetectionResult {
  columns: ColumnMapping[];
  rows: ParsedMetricRow[];
  rawData: string[][];
  warnings: string[];
  sheetName?: string;
  locale: 'de' | 'en';
  transposed: boolean;
  orientation: 'normal' | 'transposed';
}

// =============================================================================
// KEYWORD DICTIONARY
// =============================================================================

interface KeywordEntry {
  field: MetricField | '_sprintName';
  keywords: string[];
}

const COLUMN_KEYWORDS: KeywordEntry[] = [
  {
    field: '_sprintName',
    keywords: [
      'sprint', 'iteration', 'pi', 'week', 'month', 'period',
      'name', 'bezeichnung',
    ],
  },
  {
    field: 'costsPlanned',
    keywords: [
      'costs planned', 'cost plan', 'planned cost', 'budget',
      'kosten plan', 'kosten soll', 'plan kosten', 'soll kosten',
      'cost forecast', 'geplante kosten',
    ],
  },
  {
    field: 'costsActual',
    keywords: [
      'costs actual', 'cost actual', 'actual cost', 'spend',
      'kosten ist', 'ist kosten', 'actual kosten', 'tatsächliche kosten',
      'cost real',
    ],
  },
  {
    field: 'revenuePlanned',
    keywords: [
      'revenue planned', 'planned revenue', 'revenue plan', 'target revenue',
      'umsatz plan', 'umsatz soll', 'plan umsatz', 'soll umsatz',
      'geplanter umsatz', 'erlös plan',
    ],
  },
  {
    field: 'revenueActual',
    keywords: [
      'revenue actual', 'actual revenue', 'revenue real',
      'umsatz ist', 'ist umsatz', 'actual umsatz', 'tatsächlicher umsatz',
      'erlös ist',
    ],
  },
  {
    field: 'storyPointsPlanned',
    keywords: [
      'sp planned', 'story points planned', 'points planned',
      'planned sp', 'planned story points', 'planned points',
      'sp plan', 'sp soll',
    ],
  },
  {
    field: 'storyPointsTarget',
    keywords: [
      'sp target', 'story points target', 'target sp',
      'sp ziel', 'ziel sp', 'target points',
    ],
  },
  {
    field: 'storyPointsAchieved',
    keywords: [
      'sp achieved', 'sp actual', 'story points achieved', 'achieved sp',
      'sp done', 'sp completed', 'sp ist', 'ist sp',
      'actual sp', 'delivered sp', 'velocity',
    ],
  },
  {
    field: 'manDaysPlanned',
    keywords: [
      'man days planned', 'md planned', 'pt planned', 'planned md',
      'capacity planned', 'planned capacity', 'planned pt',
      'personentage plan', 'pt plan', 'pt soll', 'kapazität plan',
    ],
  },
  {
    field: 'manDaysActual',
    keywords: [
      'man days actual', 'md actual', 'pt actual', 'actual md',
      'capacity actual', 'actual capacity', 'actual pt',
      'personentage ist', 'pt ist', 'kapazität ist',
    ],
  },
];

// =============================================================================
// PARSERS (input format → string[][])
// =============================================================================

/** Parse an Excel/CSV file to a 2D string array */
export function parseFile(file: File): Promise<{ sheets: string[]; data: Map<string, string[][]> }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetsMap = new Map<string, string[][]>();

        for (const sheetName of workbook.SheetNames) {
          const sheet = workbook.Sheets[sheetName];
          const rows: string[][] = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: '',
            raw: false,
          });
          sheetsMap.set(sheetName, rows);
        }

        resolve({ sheets: workbook.SheetNames, data: sheetsMap });
      } catch {
        reject(new Error('Failed to parse file. The file may be corrupted or password-protected.'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/** Parse tab-separated values into 2D array */
export function parseTsv(text: string): string[][] {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  // Remove trailing empty lines
  while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
    lines.pop();
  }
  return lines.map((line) => line.split('\t'));
}

/** Parse HTML table into 2D array */
export function parseHtmlTable(html: string): string[][] | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const table = doc.querySelector('table');
  if (!table) return null;

  const rows: string[][] = [];
  const trElements = table.querySelectorAll('tr');

  trElements.forEach((tr) => {
    const cells: string[] = [];
    tr.querySelectorAll('td, th').forEach((cell) => {
      const colspan = parseInt(cell.getAttribute('colspan') || '1');
      const text = (cell.textContent || '').trim();
      for (let i = 0; i < colspan; i++) {
        cells.push(text);
      }
    });
    rows.push(cells);
  });

  return rows.length > 0 ? rows : null;
}

/** Try reading clipboard for HTML table data, fall back to plain text */
export async function readClipboard(): Promise<{ data: string[][]; source: 'html' | 'text' } | null> {
  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      if (item.types.includes('text/html')) {
        const blob = await item.getType('text/html');
        const html = await blob.text();
        const parsed = parseHtmlTable(html);
        if (parsed && parsed.length > 0) {
          return { data: parsed, source: 'html' };
        }
      }
      if (item.types.includes('text/plain')) {
        const blob = await item.getType('text/plain');
        const text = await blob.text();
        if (text.includes('\t')) {
          return { data: parseTsv(text), source: 'text' };
        }
      }
    }
  } catch {
    // Permission denied or API unavailable
  }
  return null;
}

// =============================================================================
// TRANSPOSE
// =============================================================================

export function transpose(data: string[][]): string[][] {
  if (data.length === 0) return [];
  const rows = data.length;
  const cols = Math.max(...data.map((r) => r.length));
  const result: string[][] = [];
  for (let c = 0; c < cols; c++) {
    result.push(Array.from({ length: rows }, (_, r) => data[r]?.[c] ?? ''));
  }
  return result;
}

// =============================================================================
// ORIENTATION DETECTION
// =============================================================================

function normalize(text: string): string {
  return text.toLowerCase().trim().replace(/[-–—]/g, ' ').replace(/[^a-zäöüß0-9\s]/g, '').replace(/\s+/g, ' ');
}

function countMetricKeywordMatches(cells: string[]): number {
  let matches = 0;
  for (const cell of cells) {
    const norm = normalize(cell);
    if (!norm) continue;
    for (const entry of COLUMN_KEYWORDS) {
      // Skip sprint name entry — only count actual metric keywords
      if (entry.field === '_sprintName') continue;
      if (entry.keywords.some((kw) => norm.includes(kw) || kw.includes(norm))) {
        matches++;
        break;
      }
    }
  }
  return matches;
}

function countSprintMatches(cells: string[], sprintNames: string[]): number {
  if (sprintNames.length === 0) return 0;
  const normalizedNames = sprintNames.map((n) => normalize(n));
  let matches = 0;
  for (const cell of cells) {
    const norm = normalize(cell);
    if (!norm) continue;
    if (normalizedNames.some((name) => norm === name || norm.replace(/\s+/g, '').replace(/\./g, '') === name.replace(/\s+/g, '').replace(/\./g, ''))) {
      matches++;
    }
  }
  return matches;
}

function getColumn(data: string[][], colIndex: number): string[] {
  return data.map((row) => row[colIndex] ?? '');
}

export function detectOrientation(data: string[][], sprintNames: string[]): 'normal' | 'transposed' {
  if (data.length === 0 || !data[0] || data[0].length === 0) return 'normal';

  // Score "normal": first row = metric headers, first column = sprint names
  let normalScore = 0;
  normalScore += countMetricKeywordMatches(data[0]);
  normalScore += countSprintMatches(getColumn(data, 0), sprintNames);

  // Score "transposed": first row = sprint names, first column = metric headers
  let transposedScore = 0;
  transposedScore += countSprintMatches(data[0], sprintNames);
  transposedScore += countMetricKeywordMatches(getColumn(data, 0));

  // Shape heuristic
  if (data.length <= 12 && data[0].length > 12) transposedScore += 2;
  if (data.length > 12 && data[0].length <= 12) normalScore += 2;

  return transposedScore > normalScore ? 'transposed' : 'normal';
}

// =============================================================================
// HEADER DETECTION
// =============================================================================

function findHeaderRow(data: string[][]): number {
  let bestRow = 0;
  let bestScore = 0;

  for (let i = 0; i < Math.min(data.length, 10); i++) {
    const score = countMetricKeywordMatches(data[i]);
    if (score > bestScore) {
      bestScore = score;
      bestRow = i;
    }
  }

  if (bestScore === 0) return -1;
  return bestRow;
}

// =============================================================================
// TWO-ROW HEADER DETECTION
// =============================================================================

function detectTwoRowHeader(data: string[][], headerRow: number): string[] | null {
  if (headerRow + 1 >= data.length) return null;

  const row1 = data[headerRow];
  const row2 = data[headerRow + 1];

  // Check if row2 has "plan/actual/soll/ist" patterns
  const planActualPatterns = ['plan', 'actual', 'soll', 'ist', 'planned', 'target', 'achieved'];
  const row2Norm = row2.map((c) => normalize(c));
  const row2HasPlanActual = row2Norm.filter((c) =>
    planActualPatterns.some((p) => c.includes(p))
  ).length;

  if (row2HasPlanActual < 2) return null;

  // Non-empty cells in row1 vs row2
  const row1NonEmpty = row1.filter((c) => c.trim()).length;
  const row2NonEmpty = row2.filter((c) => c.trim()).length;

  if (row1NonEmpty >= row2NonEmpty) return null;

  // Combine: fill empty row1 cells with the last non-empty value
  const combined: string[] = [];
  let lastGroup = '';
  for (let i = 0; i < Math.max(row1.length, row2.length); i++) {
    const top = (row1[i] || '').trim();
    const bottom = (row2[i] || '').trim();
    if (top) lastGroup = top;
    combined.push(lastGroup && bottom ? `${lastGroup} ${bottom}` : top || bottom);
  }

  return combined;
}

// =============================================================================
// COLUMN MAPPING
// =============================================================================

export function mapColumns(headers: string[]): ColumnMapping[] {
  const mappings: ColumnMapping[] = [];
  const usedFields = new Set<string>();

  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    const norm = normalize(header);

    if (!norm) {
      mappings.push({ sourceIndex: i, sourceHeader: header, field: null, confidence: 0 });
      continue;
    }

    let bestField: MetricField | '_sprintName' | null = null;
    let bestConfidence = 0;

    for (const entry of COLUMN_KEYWORDS) {
      if (usedFields.has(entry.field)) continue;

      for (const keyword of entry.keywords) {
        let confidence = 0;

        if (norm === keyword) {
          confidence = 1.0;
        } else if (norm.includes(keyword)) {
          confidence = 0.9;
        } else if (keyword.includes(norm) && norm.length >= 3) {
          confidence = 0.7;
        } else {
          // Word overlap
          const normWords = norm.split(/\s+/);
          const kwWords = keyword.split(/\s+/);
          const overlap = normWords.filter((w) => kwWords.includes(w)).length;
          if (overlap >= 2) {
            confidence = 0.7;
          } else if (overlap === 1 && kwWords.length <= 2) {
            confidence = 0.5;
          }
        }

        if (confidence > bestConfidence) {
          bestConfidence = confidence;
          bestField = entry.field;
        }
      }
    }

    if (bestField && bestConfidence >= 0.5) {
      usedFields.add(bestField);
      mappings.push({ sourceIndex: i, sourceHeader: header, field: bestField, confidence: bestConfidence });
    } else {
      mappings.push({ sourceIndex: i, sourceHeader: header, field: null, confidence: 0 });
    }
  }

  return mappings;
}

// =============================================================================
// HEADERLESS DATA HELPERS
// =============================================================================

/** Check if a column likely contains sprint names (non-numeric text or sprint pattern matches) */
function isSprintNameColumn(values: string[], sprintNames: string[]): boolean {
  if (countSprintMatches(values, sprintNames) > 0) return true;

  // Majority non-numeric text heuristic
  const nonEmpty = values.filter((v) => v.trim());
  if (nonEmpty.length === 0) return false;
  const nonNumeric = nonEmpty.filter((v) => {
    const cleaned = v.trim().replace(/[€$£¥\s.,]/g, '');
    return isNaN(Number(cleaned)) || cleaned === '';
  });
  return nonNumeric.length > nonEmpty.length / 2;
}

/** Build synthetic column mappings for headerless data */
function buildHeaderlessColumns(
  matrix: string[][],
  colCount: number,
  sprintNames: string[]
): ColumnMapping[] {
  const columns: ColumnMapping[] = [];
  // Check if column 0 looks like sprint names
  const col0Values = matrix.map((row) => (row[0] || '').trim());
  const col0IsSprint = isSprintNameColumn(col0Values, sprintNames);

  for (let i = 0; i < colCount; i++) {
    if (i === 0 && col0IsSprint) {
      columns.push({
        sourceIndex: i,
        sourceHeader: 'Column 1',
        field: '_sprintName',
        confidence: 0.5,
      });
    } else {
      columns.push({
        sourceIndex: i,
        sourceHeader: `Column ${i + 1}`,
        field: null,
        confidence: 0,
      });
    }
  }
  return columns;
}

// =============================================================================
// SPRINT MATCHING
// =============================================================================

export function matchSprints(rowValues: string[], sprints: SprintInfo[]): SprintMatch[] {
  return rowValues.map((value, rowIndex) => {
    const norm = normalize(value);
    if (!norm) {
      return { rowIndex, sourceValue: value, sprint: null, confidence: 0 };
    }

    // 1. Exact name match
    for (const sprint of sprints) {
      if (normalize(sprint.name) === norm) {
        return { rowIndex, sourceValue: value, sprint, confidence: 1.0 };
      }
    }

    // 2. Fuzzy name match (remove dots, spaces)
    const normCompact = norm.replace(/[\s.]/g, '');
    for (const sprint of sprints) {
      if (normalize(sprint.name).replace(/[\s.]/g, '') === normCompact) {
        return { rowIndex, sourceValue: value, sprint, confidence: 0.9 };
      }
    }

    // 3. Sprint number match
    const numberMatch = value.match(/(\d+)/);
    if (numberMatch) {
      const num = parseInt(numberMatch[1]);
      const match = sprints.find((s) => s.sprintNumber === num);
      if (match) {
        return { rowIndex, sourceValue: value, sprint: match, confidence: 0.7 };
      }
    }

    return { rowIndex, sourceValue: value, sprint: null, confidence: 0 };
  });
}

// =============================================================================
// NUMBER PARSING
// =============================================================================

export function detectLocale(data: string[][]): 'de' | 'en' {
  let germanPatterns = 0;
  let englishPatterns = 0;

  for (const row of data) {
    for (const cell of row) {
      if (/\d{1,3}\.\d{3}[,]/.test(cell)) germanPatterns++;
      if (/\d{1,3},\d{3}[.]/.test(cell)) englishPatterns++;
    }
  }

  return germanPatterns >= englishPatterns ? 'de' : 'en';
}

export function parseNumber(value: string, locale: 'de' | 'en' = 'de'): number | null {
  if (!value || typeof value !== 'string') return null;

  let cleaned = value.trim();
  if (cleaned === '' || cleaned === '-' || cleaned === '–' || cleaned === '—') return null;

  // Strip currency symbols and whitespace
  cleaned = cleaned.replace(/[€$£¥\s]/g, '');

  // Detect negative
  const isNegative = cleaned.startsWith('-') || cleaned.startsWith('(');
  cleaned = cleaned.replace(/^[-+(]|[)]$/g, '');

  if (locale === 'de') {
    // German: 45.000,50 → 45000.50
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // English: 45,000.50 → 45000.50
    cleaned = cleaned.replace(/,/g, '');
  }

  const num = parseFloat(cleaned);
  if (isNaN(num)) return null;

  return isNegative ? -num : num;
}

// =============================================================================
// SHEET SELECTION
// =============================================================================

export function selectBestSheet(
  sheetNames: string[],
  sheetData: Map<string, string[][]>,
  sprintNames: string[]
): string {
  if (sheetNames.length === 1) return sheetNames[0];

  let bestSheet = sheetNames[0];
  let bestScore = -1;

  const preferredNames = ['sprint', 'metrics', 'data', 'daten', 'metric'];

  for (const name of sheetNames) {
    const data = sheetData.get(name);
    if (!data || data.length === 0) continue;

    let score = 0;

    // Keyword matches in potential header rows
    for (let i = 0; i < Math.min(data.length, 3); i++) {
      score += countMetricKeywordMatches(data[i]) * 10;
    }

    // Data rows
    score += Math.min(data.length, 30) * 2;

    // Sprint name matches
    if (sprintNames.length > 0) {
      const col0 = getColumn(data, 0);
      score += countSprintMatches(col0, sprintNames) * 5;
    }

    // Sheet name preference
    const normName = name.toLowerCase();
    if (preferredNames.some((p) => normName.includes(p))) {
      score += 20;
    }

    if (score > bestScore) {
      bestScore = score;
      bestSheet = name;
    }
  }

  return bestSheet;
}

// =============================================================================
// FULL DETECTION PIPELINE
// =============================================================================

export function detectStructure(data: string[][], sprints: SprintInfo[]): DetectionResult {
  const warnings: string[] = [];

  if (data.length === 0 || (data.length === 1 && data[0].every((c) => !c.trim()))) {
    return {
      columns: [],
      rows: [],
      rawData: [],
      warnings: ['No data found in the input'],
      locale: 'de',
      transposed: false,
      orientation: 'normal',
    };
  }

  const sprintNames = sprints.map((s) => s.name);

  // 1. Detect orientation
  const orientation = detectOrientation(data, sprintNames);
  const transposed = orientation === 'transposed';
  const matrix = transposed ? transpose(data) : data;

  // 2. Find header row
  const headerRowIndex = findHeaderRow(matrix);

  // 3. Determine columns and data start
  let columns: ColumnMapping[];
  let dataStartRow: number;

  if (headerRowIndex === -1) {
    // Headerless: no metric keywords found → all rows are data
    dataStartRow = 0;
    const colCount = Math.max(...matrix.map((r) => r.length));
    columns = buildHeaderlessColumns(matrix, colCount, sprintNames);
    warnings.push('No metric headers detected — please assign columns manually');
  } else {
    // Normal: header row found
    const combinedHeaders = detectTwoRowHeader(matrix, headerRowIndex);
    const headers = combinedHeaders || matrix[headerRowIndex];
    dataStartRow = combinedHeaders ? headerRowIndex + 2 : headerRowIndex + 1;
    columns = mapColumns(headers);
  }

  // 5. Detect locale from data rows
  const dataRows = matrix.slice(dataStartRow).filter((row) => row.some((c) => c.trim()));
  const locale = detectLocale(dataRows);

  // 6. Find sprint name column
  const sprintNameCol = columns.find((c) => c.field === '_sprintName');
  const sprintColIndex = sprintNameCol?.sourceIndex ?? 0;

  // 7. Match sprints
  const sprintValues = dataRows.map((row) => (row[sprintColIndex] || '').trim());
  const sprintMatches = matchSprints(sprintValues, sprints);

  // 8. Parse metric values
  const metricColumns = columns.filter((c) => c.field !== null && c.field !== '_sprintName');
  const rows: ParsedMetricRow[] = dataRows.map((row, i) => {
    const values: Partial<Record<MetricField, number | null>> = {};
    for (const col of metricColumns) {
      const raw = row[col.sourceIndex] || '';
      values[col.field as MetricField] = parseNumber(raw, locale);
    }
    return {
      sprintMatch: sprintMatches[i] || { rowIndex: i, sourceValue: '', sprint: null, confidence: 0 },
      values,
    };
  });

  // 9. Generate warnings
  const unmatchedSprints = sprintMatches.filter((m) => !m.sprint);
  if (unmatchedSprints.length > 0) {
    warnings.push(`${unmatchedSprints.length} row(s) could not be matched to existing sprints`);
  }

  const unmappedCols = columns.filter((c) => c.field === null && c.sourceHeader.trim());
  if (unmappedCols.length > 0) {
    warnings.push(`${unmappedCols.length} column(s) were not recognized and will be ignored`);
  }

  if (!sprintNameCol) {
    warnings.push('No sprint name column detected — using first column');
  }

  return {
    columns,
    rows,
    rawData: dataRows,
    warnings,
    locale,
    transposed,
    orientation,
  };
}
